import jwtDecode from 'jwt-decode'
import { put, takeLatest, all, call, select } from 'redux-saga/effects'

import { strings } from 'context/LanguageContext/strings'
import { addLoyaltyIdForWebsite, addSubscriptionSuperProp } from 'middleware/helper'
import { setJWT, setClientID, setPhoneNumber } from 'modules/appSettings/actions'
import { getCateringData, saveCateringData } from 'utils/cateringStorage'
import {
    actions,
    appLaunchFlowComplete,
    loginSuccess,
    loginFailed,
    ResetPasswordSuccess,
    ResetPasswordFailed,
    ResetPasswordMessage,
    gettingFacebookResponse,
    facebookResponseComplete,
    facebookLoginFailed,
    facebookLoginSuccess,
    newUserFacebook,
    signOutFailed,
    checkOrderFeedbackFailed,
    signOutSuccess,
    MobileBridgeAppLaunchSuccess,
    getTokensStart,
    getTokensSuccess,
    getTokensFailed,
    getTermsAndPrivacyUpdateFailure,
    setUpdatedTermsAndConditions,
    setUpdatedPrivacyPolicy,
    patchUpdatedTermsAndPrivacyFailure,
    patchUpdatedTermsAndPrivacySuccess,
    otpGenerateSuccess,
    mfaAuthFailed,
    authLoading,
    mfaResendCodeFailed,
    refreshAppFailed,
    refreshAppSuccess,
} from 'modules/authentication/actions'
import {
    passwordResetURL,
    newMapiRefreshTokenURL,
    getUpdatedTermsAndPrivacyURL,
    patchUpdateTermsAndPrivacyURL,
    missingFeedbackUrl,
    socialAuthURL,
    loggingUrl,
    getClientTokensforSignin,
    updateUserProfileURL,
    getTokenPaypalVenmoURL,
    mfaAuthOTPGenerate,
    mfaAuthOTPVerify,
    revokeTokenURL,
} from 'modules/authentication/urls'
import { setBasketUpdates } from 'modules/basket/actions'
import { getCart, updateShipping } from 'modules/basket/sagas'
import { getConfiguration } from 'modules/configuration/actions'
import {
    getPickupStores,
    setAddress,
    setStoreDetails,
    setSavedAddresses,
    setAlcoholRestriction,
    getAlcoholRestriction,
    setAddressProcessing,
} from 'modules/location/actions'
import {
    displayFeedbackModal,
    stopPollingForOrderStatus,
    getCurrentOrdersList,
    setActiveOrdersCount,
} from 'modules/orders/actions'
import { getCategoryFilters, getHomePageItems, getStoreAddress } from 'modules/products/actions'
import { resetSubscriptions } from 'modules/subscription/actions'
import {
    getUserProfile,
    resetUser,
    setUserProfile,
    updateUserProfileFailed,
} from 'modules/user/actions'
import { resetWallet } from 'modules/wallet/actions'
import { api } from 'network/api'
import {
    getClientId,
    getClientSecret,
    getGuestClientID,
    getGuestSecretID,
    getPasswordClientID,
    getPasswordSecretID,
} from 'network/api/networkSettings'
import { util } from 'utils'
import { getJWTDetails, orderTypes, isAppRefreshRequired, isEmpty } from 'utils/appUtils'

const MINUTE = 1000 * 60
const TOKEN_REFRESH_PADDING = 30 * MINUTE

function* appLaunch(access = null, refresh = null) {
    try {
        // If there are tokens params, the method is called from bridge for initial call
        const { appSettings } = yield select()
        const isApp = appSettings.get('isApp')
        // yield trackRewardsTokens(jwt, refreshToken)
        if (access && refresh) {
            yield* proceedWithSignedInFlow()
            yield* proceedAppLaunch()
            yield put(MobileBridgeAppLaunchSuccess(true))
        } else {
            yield* regularAppLaunchFlow()
        }
    } catch (error) {
        console.log(' Inside appLaunch ', error)
        yield put(appLaunchFlowComplete())
    }
}

function* regularAppLaunchFlow() {
    try {
        const { tokenExists, token, refreshToken, isSignedIn } = getJWTDetails()
        // no token flow
        if (!tokenExists) {
            const success = yield* getGuestToken()
            if (success) {
                yield* proceedWithGuestFlow()
            }
            return
        }
        if (isSignedIn) {
            const refreshRequired = yield tokenRefreshRequired(refreshToken)
            if (refreshRequired) {
                yield* refreshJWT(refreshToken)
            }
            yield* proceedWithSignedInFlow()
            yield* proceedAppLaunch()
        } else {
            const isGuestTokenRefreshRequired = yield tokenRefreshRequired(token)
            if (isGuestTokenRefreshRequired) {
                const success = yield* getGuestToken()
                if (success) {
                    yield* proceedWithGuestFlow()
                    yield* proceedAppLaunch()
                }
            } else {
                yield* proceedWithGuestFlow()
                yield* proceedAppLaunch()
            }
        }
        // yield* proceedAppLaunch()
    } catch (error) {
        console.log(' Regular app launch failed ', error)
    }
}

function* proceedAppLaunch() {
    // After app launch flow , the subsequent checks should be based on the token
    yield put(getPickupStores())
    yield put(getConfiguration())
    yield put(appLaunchFlowComplete())
    yield put(setBasketUpdates({}))
}

function* proceedWithGuestFlow() {
    try {
        yield put(getHomePageItems())
        // If the location exists, then patch the store/address
        yield updateShipping()
        yield* proceedAppLaunch()
        yield* commonFlow()
    } catch (error) {
        console.log(' Guest flow failed ', error)
    }
}

function* proceedWithSignedInFlow() {
    const { appSettings } = yield select()
    const isApp = appSettings.get('isApp')
    try {
        // First Load home page items
        if (!isApp || !window.location.href.includes('checkout')) {
            yield put(getStoreAddress())
            yield put(getUserProfile()) // Address + Home Page Items
        }
    } catch (error) {
        console.log(' Initial Signed In flow failed ', error)
    }
}

export function* commonFlow() {
    try {
        yield* getCart()
    } catch (error) {
        console.log(' Failed in common Flow ', error)
    }
}

// eslint-disable-next-line require-yield
export function* getJwtType(jwt) {
    if (jwt) {
        const decodedHeader = jwtDecode(jwt, { header: true })
        console.log('header', decodedHeader)
        if (decodedHeader && decodedHeader.alg && decodedHeader.alg === 'HS256') {
            return true
        }
    }
    return false
}

function* tokenRefreshRequired(jwt) {
    try {
        const { exp } = jwtDecode(jwt) // Convert the token expiration from seconds to milli-seconds
        const isAboutToExpire = exp * 1000 <= Date.now() + TOKEN_REFRESH_PADDING
        return isAboutToExpire
    } catch (error) {
        const errorLog = JSON.stringify(error)
        yield trackTokenInvalid(jwt, errorLog)
        return false
    }
}

function* trackTokenInvalid(jwt, error) {
    try {
        yield call(api, loggingUrl, 'POST', {
            message: jwt + error,
            logLevel: 'error',
            loggedBy: 'webAppAPI',
        })
    } catch (error) {
        console.log(' error while logging ', error)
    }
}

function* refreshJWT(refreshJwtToken) {
    try {
        const response = yield call(
            api,
            newMapiRefreshTokenURL,
            'POST',
            { refresh_token: refreshJwtToken },
            0,
            10,
            true
        )
        const { access_token, refresh_token } = response
        yield put(
            setJWT({
                access_token,
                refresh_token,
            })
        )
        return response || null
    } catch (error) {
        console.log(' Failed to refresh token ', error)
        return null
    }
}

export function* getGuestToken() {
    try {
        const uniqueId = yield* getGuestUniqueID()
        const request = {
            client_id: getGuestClientID(),
            client_secret: getGuestSecretID(),
            digital_id: uniqueId,
            grant_type: 'client_credential',
        }
        const response = yield call(api, getClientTokensforSignin, 'POST', request, 0, 10, true)
        if (response && response.access_token) {
            const { access_token } = response
            yield put(setJWT({ access_token, refresh_token: null }))
            return true
        }
        yield put(setJWT({ access_token: null, refresh_token: null }))
        return false
    } catch (error) {
        yield put(setJWT({ access_token: null, refresh_token: null }))
        return false
    }
}

export function* patchGuestProfile(guestId) {
    const request = {
        guest_id: guestId,
    }
    try {
        const response = yield call(api, updateUserProfileURL, 'PATCH', request)
        const { error_message } = response
        if (error_message) {
            yield put(updateUserProfileFailed({ message: error_message }))
        }
    } catch (error) {
        console.log(' Failed to patch guest user profile ', error)
    }
}

export function* getGuestUniqueID() {
    try {
        const { appSettings } = yield select()
        const identifier = appSettings.get('clientID')

        if (identifier) {
            return identifier
        } else {
            const { v4: uuidv4 } = require('uuid')
            const unique_identifier = uuidv4()

            yield put(setClientID(unique_identifier))
            return unique_identifier
        }
    } catch (error) {
        return ''
    }
}

function* login(action) {
    try {
        const {
            payload: { userName, password },
        } = action

        const tokenResponse = yield* createClientTokens({
            email: userName,
            password,
        })

        if (tokenResponse && !tokenResponse.error) {
            const { access_token, refresh_token } = tokenResponse
            if (!access_token || !refresh_token) {
                throw new Error('NO TOKENS IN LOGIN')
            }
            yield* parseGetTokens(tokenResponse)
        } else {
            yield put(loginFailed(tokenResponse.error_description || strings.loginFailure))
        }
    } catch (error) {
        console.log(' Sigin failed ', error)
        yield put(loginFailed(strings.loginFailure))
    }
}

function* mfaLogin(action) {
    try {
        const {
            payload: { userName, password },
        } = action

        yield put(authLoading(true))
        const otpResponse = yield* mfaAuth({
            email: userName,
            password,
        })
        yield put(authLoading(false))
        if (otpResponse) {
            const { digital_id, mobile_number, is_sms_otp_sent } = otpResponse
            if (!digital_id) {
                throw new Error('UNABLE TO GET THE OTP')
            }
            yield put(
                otpGenerateSuccess({
                    data: {
                        digital_id,
                        mobile_number,
                        is_sms_otp_sent,
                    },
                    success: true,
                })
            )
        } else {
            yield put(loginFailed(strings.loginFailure))
        }
    } catch (error) {
        yield put(authLoading(false))
        console.log(' Sigin failed ', error)
        yield put(mfaAuthFailed(error))
    }
}

function* mfaAuth(action) {
    const request = {
        client_id: getPasswordClientID(),
        client_secret: getPasswordSecretID(),
        grant_type: 'password',
        username: action.email,
        password: action.password,
    }
    try {
        const response = yield call(api, mfaAuthOTPGenerate, 'POST', request, 0, 20, true)
        return response
    } catch (e) {
        yield put(mfaAuthFailed(e))
        console.log(e)
    }
}

function* mfaVerify(action) {
    try {
        const {
            payload: { digital_id, code },
        } = action
        yield put(authLoading(true))
        const otpResponse = yield* mfaVerifyInvoke({
            digital_id,
            code,
        })

        if (otpResponse) {
            const { access_token, refresh_token } = otpResponse
            if (!access_token || !refresh_token) {
                throw new Error('NO TOKENS IN LOGIN')
            }
            yield* parseGetTokens(otpResponse)
        } else {
            yield put(loginFailed(strings.loginFailure))
        }
        yield put(authLoading(false))
    } catch (error) {
        console.log(' Sigin failed ', error)
        if (!action.code) {
            yield put(mfaResendCodeFailed(error))
        } else {
            yield put(mfaAuthFailed(error))
        }
        yield put(authLoading(false))
    }
}

function* mfaVerifyInvoke(action) {
    const request = {
        client_id: getPasswordClientID(),
        client_secret: getPasswordSecretID(),
        digital_id: action.digital_id,
        code: action.code,
    }
    try {
        yield put(getTokensStart())
        const response = yield call(api, mfaAuthOTPVerify, 'POST', request, 0, 20, true)
        yield put(getTokensSuccess(response))
        return response
    } catch (e) {
        yield put(getTokensFailed(e))
        if (!action.code) {
            yield put(mfaResendCodeFailed(e))
        } else {
            yield put(mfaAuthFailed(e))
        }
        console.log(e)
    }
}

function* createClientTokens(action) {
    if (!action.email || !action.password) {
        return
    }

    const request = {
        client_id: getClientId(),
        client_secret: getClientSecret(),
        grant_type: 'password',
        username: action.email,
        password: action.password,
    }
    try {
        yield put(getTokensStart())
        const response = yield call(api, getClientTokensforSignin, 'POST', request, 0, 20, true)
        yield put(getTokensSuccess(true))
        return response
    } catch (e) {
        yield put(getTokensFailed(e))
        yield put(loginFailed(strings.loginFailure))
        console.log(e)
        return e.data
    }
}

// eslint-disable-next-line require-yield
function* revokeToken(action) {
    const request = {
        client_id: getClientId(),
        client_secret: getClientSecret(),
        token: action.payload,
    }
    try {
        util.revokeTokenUtility(revokeTokenURL, request)
    } catch (error) {
        console.log('Error is', error)
    }
}

function* loginWithFb(action) {
    const response = action.payload
    try {
        if (response.accessToken) {
            const { email } = response
            const fbLoginRequest = util.signInWithFbRequest(response.accessToken, email)
            // Set the flag to show the spinner for FB login
            yield put(gettingFacebookResponse())
            const socialAuthResponse = yield call(
                api,
                socialAuthURL,
                'POST',
                fbLoginRequest,
                0,
                10,
                true
            )
            if (socialAuthResponse) {
                const { access_token, refresh_token } = socialAuthResponse
                if (!access_token || !refresh_token) {
                    throw new Error('NO TOKENS IN RESPONSE')
                }
                yield* parseGetTokens(socialAuthResponse)
                yield put(facebookLoginSuccess(response))
            }
            yield put(facebookResponseComplete())
        } else {
            throw new Error('FB TOKEN MISSING')
        }
    } catch (error) {
        if (error && error.data && error.data.error === 'social_login_error') {
            const { accessToken, first_name, last_name, email } = response
            yield put(newUserFacebook({ accessToken, first_name, last_name, email }))
        } else {
            yield put(facebookResponseComplete())
            yield put(facebookLoginFailed(error))
        }
    }
}

function* resetPassword(action) {
    const { email, callback } = action.payload

    try {
        const response = yield call(api, passwordResetURL, 'POST', email)

        if (response?.error_message) {
            yield put(ResetPasswordFailed(response.error_message))
            if (callback) callback(response) // Call callback with the response
        } else {
            const resetPasswordMessage = response.error_message
                ? response.error_message
                : ' reset your password'

            yield put(ResetPasswordMessage(resetPasswordMessage))
            yield put(ResetPasswordSuccess())
            if (callback) callback(null)
        }
    } catch (error) {
        yield put(ResetPasswordFailed(error))
    }
}

export function* parseRegisterResponse(response) {
    try {
        const { token, profile, refresh_token } = response
        if (profile && profile.active_orders_count) {
            yield put(setActiveOrdersCount(profile.active_orders_count))
        }
        const parsedProfile = util.parseUserProfile(profile)
        yield put(setUserProfile(parsedProfile))
        if (!isEmpty(parsedProfile)) {
            addLoyaltyIdForWebsite(parsedProfile)
            if (!isEmpty(parsedProfile?.subscriptions)) {
                addSubscriptionSuperProp(parsedProfile?.subscriptions?.[0])
            }
        }
        if (token && !refresh_token) {
            yield put(setJWT({ access_token: token, refresh_token: null }))
        } else {
            yield put(setJWT({ access_token: token, refresh_token }))
        }
        yield put(loginSuccess(token))
        yield* setAddressFromProfileResponse(response)
    } catch (error) {
        console.log(' Error is ', error)
    }
}

export function* parseGetTokens(response) {
    if (response) {
        const { access_token, refresh_token } = response
        const { isConversion, customer_id } = util.isConversionRequired()
        
        // Preserve catering data before clearing session storage
        const cateringData = yield call(getCateringData)
        
        yield put(setJWT({ access_token, refresh_token }))

        if (isConversion) {
            yield patchGuestProfile(customer_id)
        }
        if (sessionStorage) {
            sessionStorage.clear()
        }
        
        // Restore catering data after clearing session storage
        if (cateringData.address || cateringData.schedule || cateringData.store) {
            yield call(saveCateringData, cateringData.address, cateringData.schedule, cateringData.store)
        }
        
        yield put(getStoreAddress())
        yield put(getUserProfile())
        yield put(loginSuccess(access_token))
    }
}

export function* setAddressFromProfileResponse(response) {
    try {
        const { recent_address, saved_address, stores } = response
        const { products } = yield select()
        // If we found any recent address with associated store which is NOT HOTPOT address
        if (
            recent_address &&
            stores &&
            recent_address[0] &&
            stores[0] &&
            !recent_address.hotspotId
        ) {
            // if (current_order_type) yield put(setOrderType(current_order_type))
            const parsedAddress = util.getCommonFormattedAddress(recent_address[0])
            // if (parsedAddress.country !== countryCode.CA) {
            yield all([
                put(setAddress(parsedAddress)),
                put(setStoreDetails(stores[0])),
                // put(setLocationConfirmed(true)),
            ])
            if (parsedAddress.type === orderTypes.DELIVERY) {
                yield put(getAlcoholRestriction(recent_address[0]))
            } else {
                yield put(setAlcoholRestriction(null))
            }
            // }
            if (saved_address) {
                const parsedSavedAddress = saved_address.map(each =>
                    util.getCommonFormattedAddress(each)
                )
                yield put(setSavedAddresses(parsedSavedAddress))
            } else {
                yield put(setSavedAddresses([]))
            }
        } else {
            yield put(setAddress(null))
            yield put(setStoreDetails(null))
        }
        if (products && !products.getHomePageItemsInProgress) {
            yield put(getHomePageItems())
        }
        yield put(setAddressProcessing())
        yield updateShipping()
        // eslint-disable-next-line no-empty
    } catch (e) {}
}

function* refreshAppIfRequired(payload) {
    try {
        if (isAppRefreshRequired() || payload?.payload) {
            yield put(getConfiguration())
            const { isSignedIn } = util.getJWTDetails()
                const updatedTerms = yield* getTermsAndPrivacyUpdate()
                if (updatedTerms && (updatedTerms.terms || updatedTerms.privacy)) return
                // Hiding feedback overlay
                // const showOrderFeedback = yield* orderFeedbackFlow()
                // if (!showOrderFeedback) yield put(getHomePageItems())

                yield put(getHomePageItems())
                yield put(getCurrentOrdersList())
                yield* getCart()
        }
        yield put(stopPollingForOrderStatus())
        yield put(refreshAppSuccess())
    } catch (error) {
        console.log(' Refresh app error ', error)
        yield put(refreshAppFailed())
    }
}

function* getTermsAndPrivacyUpdate() {
    try {
        const response = yield call(api, getUpdatedTermsAndPrivacyURL, 'GET')
        const { terms, privacy } =
            response.required_terms && Array.isArray(response.required_terms)
                ? response.required_terms.reduce(
                      (acc, { id, type, url }) =>
                          type === 'privacy' || type === 'terms'
                              ? { ...acc, [type]: { id, url } }
                              : acc,
                      {}
                  )
                : {}
        if (terms) yield put(setUpdatedTermsAndConditions(terms))
        else yield put(setUpdatedTermsAndConditions(null))
        if (privacy) yield put(setUpdatedPrivacyPolicy(privacy))
        else yield put(setUpdatedPrivacyPolicy(null))
        return { terms, privacy }
    } catch (error) {
        yield put(getTermsAndPrivacyUpdateFailure(error))
        return null
    }
}

export function* patchUpdatedTermsAndPrivacy(action) {
    const { payload } = action
    try {
        const request = { accepted_terms: payload }
        yield call(api, patchUpdateTermsAndPrivacyURL, 'POST', request)
        yield put(patchUpdatedTermsAndPrivacySuccess(true))
    } catch (error) {
        yield put(patchUpdatedTermsAndPrivacyFailure(true))
    }

    yield put(getHomePageItems())
}

export function* orderFeedbackFlow() {
    try {
        const order = yield call(api, missingFeedbackUrl, 'GET', null)
        if (order && order.order_id) {
            yield put(displayFeedbackModal(order))
            return true
        }
        return false
    } catch (error) {
        console.log('Some error occurred for order feedback')
        yield put(checkOrderFeedbackFailed(error))
        return false
    }
}

export function* signOut({ navigate }) {
    try {
        yield all([
            put(setJWT({ access_token: null, refresh_token: null })),
            put(resetUser()),
            put(patchUpdatedTermsAndPrivacySuccess(false)),
            put(patchUpdatedTermsAndPrivacyFailure(false)),
            put(setUpdatedPrivacyPolicy(null)),
            put(setUpdatedTermsAndConditions(null)),
            put(setPhoneNumber(null)),
            put(setSavedAddresses([])),
            put(resetWallet()),
            put(resetSubscriptions()),
        ])
        navigate('/home')
        yield put(signOutSuccess())
        yield* appLaunch()
    } catch (error) {
        yield put(signOutFailed(error))
    }
}

function* guestTokenWatcher() {
    yield takeLatest(actions.GET_GUEST_TOKEN, getGuestToken)
}

function* appLaunchWatcher() {
    yield takeLatest(actions.APP_LAUNCH, appLaunch)
}

function* loginWatcher() {
    yield takeLatest(actions.LOGIN, login)
}

function* loginWithFbWatcher() {
    yield takeLatest(actions.LOGIN_WITH_FB, loginWithFb)
}

function* resetPasswordWatcher() {
    yield takeLatest(actions.SEND_RESET_PASSWORD_EMAIL, resetPassword)
}

function* refreshAppWatcher() {
    yield takeLatest(actions.REFRESH_APP, refreshAppIfRequired)
}

function* mfaAuthWatcher() {
    yield takeLatest(actions.MFA_AUTH, mfaLogin)
}

function* mfaVerifyWatcher() {
    yield takeLatest(actions.MFA_VERIFY, mfaVerify)
}

function* signOutWatcher() {
    yield takeLatest(actions.SIGN_OUT, signOut)
}
function* patchUpdatedTermsAndPrivacyWatcher() {
    yield takeLatest(actions.PATCH_UPDATED_TERMS_AND_PRIVACY, patchUpdatedTermsAndPrivacy)
}

// function* getBraintreeClientTokenWatcher() {
//     yield takeLatest(actions.GET_BRAINTREE_CLIENT_TOKEN, getBraintreeClientToken)
// }

function* revokeTokenWatcher() {
    yield takeLatest(actions.REVOKE_TOKEN, revokeToken)
}

export default function* authenticationSaga() {
    yield all([
        loginWatcher(),
        loginWithFbWatcher(),
        appLaunchWatcher(),
        guestTokenWatcher(),
        resetPasswordWatcher(),
        refreshAppWatcher(),
        signOutWatcher(),
        patchUpdatedTermsAndPrivacyWatcher(),
        // getBraintreeClientTokenWatcher(),
        mfaAuthWatcher(),
        mfaVerifyWatcher(),
        revokeTokenWatcher(),
    ])
}
